<template>
  <main class="relative min-h-[100vh]">
    <GridsOutterGrid>
      <SectionSet is-first class="col-span-1 col-start-2">
        <GridsTwelveGrid>
          <BreadCrumbs
            class="col-span-6 col-start-1 flex capitalize laptop:col-span-1 laptop:col-start-11 laptop:justify-end laptop:text-right"
          />
          <h1 class="col-span-6 col-start-1 capitalize laptop:col-span-7 laptop:col-start-5 laptop:text-right">
            {{ $t("stay-connected-with-intercomm") }} <br />
            {{ $t("and-see-where-you-can-find-us-next") }}
          </h1>
        </GridsTwelveGrid>
      </SectionSet>

      <div class="relative col-span-3 col-start-1">
        <GridsOutterGrid>
          <GridsTwelveGrid class="col-span-3 col-start-1 laptop:col-start-2 laptop:col-end-3">
            <NewsEventSlider
              class="col-span-6 col-start-1 laptop:col-span-10 laptop:col-start-2"
              :events="eventPageData"
              @openEventModal="openEventModal"
            />
          </GridsTwelveGrid>
          <div class="relative col-span-3 col-start-1 overflow-hidden">
            <img
              src="/images/backgrounds/news-bg.png"
              alt=""
              ref="newsBg"
              class="pointer-events-none absolute top-25 left-0 -z-10 w-full select-none laptop:top-36"
            />

            <GridsOutterGrid>
              <GridsTwelveGrid class="col-span-1 col-start-2">
                <SectionSet isLast class="relative col-span-6 col-start-1 laptop:col-span-12 laptop:col-start-1">
                  <GridsTwelveGrid class="col-span-full col-start-1">
                    <h2 class="col-span-full col-start-1 desktop:col-span-7 desktop:col-start-5">
                      {{ $t("our-news") }}
                    </h2>
                    <div
                      class="col-span-full col-start-1 flex flex-col items-start space-y-8 desktop:col-span-7 desktop:col-start-5 desktop:space-y-16"
                    >
                      <TransitionGroup name="list">
                        <NewsNewsCard
                          v-for="(post, index) in data"
                          :news="post"
                          :key="post.id as string"
                          :index="index"
                        />
                      </TransitionGroup>
                      <SeeMoreBtn
                        @click="start += blogPostLimit"
                        :disabled="!hasMore || loading"
                        :class="{
                          'opacity-50': !hasMore || loading,
                          'cursor-not-allowed': !hasMore,
                          'cursor-progress': loading,
                        }"
                        class="mx-auto text-[15px] laptop:ml-auto laptop:mr-0"
                      >
                        {{ $t("see-more") }}
                      </SeeMoreBtn>
                    </div>
                  </GridsTwelveGrid>
                </SectionSet>
              </GridsTwelveGrid>
            </GridsOutterGrid>
          </div>
        </GridsOutterGrid>
      </div>
    </GridsOutterGrid>
    <ModalsEventModal :event="modalEvent" :isOpen="openModal" @close="triggerModalOpen(false)" />
  </main>
</template>
<script setup lang="ts">
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { EventInformationFragment } from "#gql";

const { promise } = useWaitForPageTransitionEnd();
const pageTitle = ref<string | null>(null);
const { t: $t } = useI18n();
const { locale, finalizePendingLocaleChange } = useI18n();
const start = ref(0);
const blogPostLimit = ref(4);

await finalizePendingLocaleChange();

const { data: eventPageData } = await useAsyncGql({
  operation: "NewsPage",
  variables: { locale: locale.value },
  options: {
    lazy: false,
  },
});

watch(
  eventPageData,
  (newVal) => {
    if (newVal) {
      if (newVal.newsPage?.data?.attributes?.seo?.metaTitle) {
        pageTitle.value = newVal.newsPage?.data?.attributes?.seo?.metaTitle;
      }
      useHead({
        title: () => pageTitle.value,
      });

      useServerSeoMeta({
        title: () => `${newVal.newsPage?.data?.attributes?.seo?.metaTitle}`,
        ogTitle: () => `${newVal.newsPage?.data?.attributes?.seo?.metaTitle}`,
        description: () => `${newVal.newsPage?.data?.attributes?.seo?.metaDescription}`,
        ogDescription: () => `${newVal.newsPage?.data?.attributes?.seo?.metaDescription}`,
        ogImage: () => `${newVal.newsPage?.data?.attributes?.seo?.metaImage?.data?.attributes?.url}`,
      });
    }
  },
  { immediate: true },
);

const newsBg = ref<HTMLElement | null>(null);

const { data, loading, hasMore } = useNews("BlogPosts", start, blogPostLimit, locale);

onMounted(() => {
  initAnimation();
});

let tween = null;
async function initAnimation() {
  await promise.value;

  if (newsBg.value) {
    tween = gsap.to(newsBg.value, {
      scrollTrigger: {
        trigger: newsBg.value,
        start: "top 80%",
        end: "bottom 80%",
        scrub: 2,
      },
      yPercent: -15,
    });
  }
}

const modalEvent = ref<{ event: EventInformationFragment; date: string } | null>(null);

const openModal = ref(false);

function triggerModalOpen(value: boolean) {
  openModal.value = value;
}

function openEventModal(eventInfo: { event: EventInformationFragment; date: string }) {
  modalEvent.value = eventInfo;
  triggerModalOpen(true);
}

onBeforeUnmount(() => {
  if (tween) {
    tween.kill;
    tween = null;
  }
  // ScrollTrigger.killAll();
});
</script>
<style lang="scss" scoped>
.list-enter-active,
.list-leave-active {
  transition: all 0.4s ease-out;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
